<template>
  <div class="order-invoice">
    <div v-if="status == 'success'">
      <div v-if="show_heading">
        <h1>{{ $t('order.thank_you') }}</h1>
        <h2>{{ $t('order.order') }} #{{ order.invoice_no }}</h2>
        <hr />
      </div>
      <table>
        <tr>
          <td>
            <strong>{{ $t('order.customer') }}:</strong>
          </td>
          <td>{{ order.customer }}</td>
        </tr>
        <tr>
          <td>
            <strong>{{ $t('order.invoice_no') }}:</strong>
          </td>
          <td>{{ order.invoice_no }}</td>
        </tr>
        <tr>
          <td>
            <strong>{{ $t('order.invoice_date') }}:</strong>
          </td>
          <td>{{ order.invoice_date }}</td>
        </tr>
        <tr>
          <td>
            <strong>{{ $t('order.shipping_method') }}:</strong>
          </td>
          <td>{{ order.ship_method.name[$i18n.locale] }}</td>
        </tr>
        <tr>
          <td>
            <strong>{{ $t('order.payment_method') }}:</strong>
          </td>
          <td>{{ order.pay_method.name[$i18n.locale] }}</td>
        </tr>
        <tr>
          <td>
            <strong>{{ $t('order.order_price') }}:</strong>
          </td>
          <td>{{ order.total }} {{ order.currency.iso }}</td>
        </tr>
        <tr>
          <td colspan="2">
            <hr />
          </td>
        </tr>
        <tr>
          <td colspan="2">
            <table class="order-products">
              <thead>
                <tr>
                  <td>
                    <strong>{{ $t('order.product') }}</strong>
                  </td>
                  <td>
                    <strong>{{ $t('order.sku') }}</strong>
                  </td>
                  <td width="100">
                    <strong>{{ $t('order.qty') }}</strong>
                  </td>
                  <td width="180">
                    <strong>{{ $t('order.price') }}</strong>
                  </td>
                  <td width="180">
                    <strong>{{ $t('order.total') }}</strong>
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in order.products" :key="product.id">
                  <td>
                    <router-link :to="$i18nRoute({name: 'product', params: {product: product.id}})">
                      {{
                      product.pivot.name
                      }}
                    </router-link>
                  </td>
                  <td>{{ product.pivot.sku }}</td>
                  <td>{{ product.pivot.quantity }}</td>
                  <td>{{ product.pivot.price }} {{ order.currency.iso }}</td>
                  <td>
                    {{ product.pivot.price * product.pivot.quantity }}
                    {{ order.currency.iso }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2"></td>
                  <td colspan="3">
                    <table>
                      <tr>
                        <td>{{ $t('order.subtotal') }}</td>
                        <td>{{ order.subtotal }} {{ order.currency.iso }}</td>
                      </tr>
                      <tr v-if="order.total_shipping > 0">
                        <td>{{ $t('order.shipping') }}</td>
                        <td>{{ order.total_shipping }} {{ order.currency.iso }}</td>
                      </tr>
                      <tr v-if="order.total_discount > 0">
                        <td>{{ $t('order.discount') }}</td>
                        <td>{{ order.total_discount }} {{ order.currency.iso }}</td>
                      </tr>
                      <tr v-if="order.total_coupon > 0">
                        <td>{{ $t('order.coupon') }}</td>
                        <td>{{ order.total_coupon }} {{ order.currency.iso }}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>{{ $t('order.grand_total') }}</strong>
                        </td>
                        <td>{{ order.grand_total }} {{ order.currency.iso }}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </tfoot>
            </table>
          </td>
        </tr>
        <tr>
          <td colspan="2">
            <hr />
          </td>
        </tr>
        <tr>
          <td>
            <h2>{{ $t('order.billing_address') }}</h2>
            <table>
              <tr>
                <td>
                  <strong>{{ $t('order.name') }}</strong>
                </td>
                <td>
                  {{ order.billing_address.first_name }}
                  {{ order.billing_address.last_name }}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.address') }}</strong>
                </td>
                <td>
                  {{ order.billing_address.address_1 }}
                  <span
                    v-if="order.billing_address.address_2 != ''"
                  >
                    <br />
                    {{ order.billing_address.address_2 }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.country') }}</strong>
                </td>
                <td>{{ order.billing_address.country.name }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.city') }}</strong>
                </td>
                <td>{{ order.billing_address.city }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.postal_code') }}</strong>
                </td>
                <td>{{ order.billing_address.zip_code }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.phone') }}</strong>
                </td>
                <td>{{ order.billing_address.phone }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.email') }}</strong>
                </td>
                <td>{{ order.billing_address.email }}</td>
              </tr>
            </table>
          </td>
          <td>
            <h2>{{ $t('order.shipping_address') }}</h2>
            <table>
              <tr>
                <td>
                  <strong>{{ $t('order.name') }}</strong>
                </td>
                <td>
                  {{ order.shipping_address.first_name }}
                  {{ order.shipping_address.last_name }}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.address') }}</strong>
                </td>
                <td>
                  {{ order.shipping_address.address_1 }}
                  <span
                    v-if="order.shipping_address.address_2 != ''"
                  >
                    <br />
                    {{ order.shipping_address.address_2 }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.country') }}</strong>
                </td>
                <td>{{ order.shipping_address.country.name }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.city') }}</strong>
                </td>
                <td>{{ order.shipping_address.city }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.postal_code') }}</strong>
                </td>
                <td>{{ order.shipping_address.zip_code }}</td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('order.phone') }}</strong>
                </td>
                <td>{{ order.shipping_address.phone }}</td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </div>
    <span v-else>Loading...</span>
  </div>
</template>

<script>
import Api from '@/services/api-service';
export default {
  name: 'invoice',
  props: ['invoice_no', 'show_heading'],
  data () {
    return {
      order: false,
      status: false,
      payment_status: ''
    }
  },
  created () {

    if(this.invoice_no) {
      Api.post('get-order', {
        invoice_no: this.invoice_no
      })
      .then(response => {
        if (response.data.status == 'success') {
          this.order = response.data.order

          if(this.order.grand_total === '0.00'){
            this.status = 'success'
          } else if (this.order.pay_method.key === 'credit-card' || this.order.pay_method.key === 'idram') {
            this.payment_status = response.data.payment_status
            if (this.payment_status === 'success') {
              this.status = 'success'
            } else {
              this.$store.dispatch('fetchNotification', {
                title: 'payment_not_accept',
                content: 'invalid_payment'
              })
              this.$router.push({ name: 'notify', params: { lang: this.$i18n.locale } })
            }
          } else {
            this.status = 'success'
          }
        } else {
          this.$router.push({ name: '404' })
        }
      })
      .catch(error => {
        this.loading = false
        if (error.response.status === 419) {
           window.location.href = '/' + this.$i18n.locale + '/sign-in'
        } else {
           this.$router.push({ name: '404', params: {lang: this.$i18n.locale}})
        }
      })
    }
  }
}
</script>

<style lang="scss">
.order-invoice {
  padding-top: 120px;
  padding-bottom: 70px;
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    &.order-products {
      tbody {
        tr {
          &:nth-child(2n + 1) {
            background: rgba(0, 0, 0, 0.1);
          }
          td {
            a {
              color: #000;
            }
          }
        }
      }
      td {
        padding: 10px;
        &:nth-child(4),
        &:nth-child(5) {
          text-align: right;
        }
      }
      tfoot {
        text-align: right;
        table {
          border-top: 1px solid #000000;
        }
      }
    }

    td {
      padding: 7px 0;
      vertical-align: top;
    }
    th {
      text-align: left;
    }
    h2 {
      margin: 0;
      font-size: 18px;
      margin-bottom: 15px;
      font-family: $font-medium;
    }
  }
}
</style>
